import { leaveService } from "@/api/leaves";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { LeaveApplication } from "@/types";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { formatLeaveType } from "@/utils/leaveType";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, XCircle } from "lucide-react";
import { useState } from "react";

export default function Manager() {
  const { user, isSuperHR } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedLeave, setSelectedLeave] = useState<LeaveApplication | null>(
    null,
  );
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  const { data: pendingLeaves = [], isLoading } = useQuery({
    queryKey: ["pending-leaves"],
    queryFn: async () => {
      if (isSuperHR) {
        return leaveService.getPendingLeaves();
      }
      return leaveService.getPendingLeaves();
    },
  });

  const { data: pastLeaves = [], isLoading: isLoadingPast } = useQuery({
    queryKey: ["past-leaves", user?.id],
    queryFn: () => leaveService.getPastLeaves(user!.id),
    enabled: !!user?.id,
  });

  const approveMutation = useMutation({
    mutationFn: leaveService.approve,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-leaves"] });
      queryClient.invalidateQueries({ queryKey: ["past-leaves"] });
      toast({
        title: "Leave approved",
        description: "Leave request has been approved",
      });
    },
    onError: (error: unknown) => {
      const message = getErrorMessage(error, "Failed to approve leave");
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      leaveService.reject(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-leaves"] });
      queryClient.invalidateQueries({ queryKey: ["past-leaves"] });
      toast({
        title: "Leave rejected",
        description: "Leave request has been rejected",
      });
      setShowRejectDialog(false);
      setRejectReason("");
      setSelectedLeave(null);
    },
    onError: (error: unknown) => {
      const message = getErrorMessage(error, "Failed to reject leave");
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    },
  });

  const handleApprove = (leave: LeaveApplication) => {
    approveMutation.mutate(leave.id);
  };

  const handleReject = () => {
    if (!selectedLeave || !rejectReason.trim()) {
      toast({
        title: "Error",
        description: "Please provide a reason for rejection",
        variant: "destructive",
      });
      return;
    }

    rejectMutation.mutate({ id: selectedLeave.id, reason: rejectReason });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold text-foreground">
            Pending Approvals
          </h2>
          <p className="text-muted-foreground mt-1">
            Review and manage leave requests from your team
          </p>
        </div>

        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle>Leave Requests</CardTitle>
            <CardDescription>
              {pendingLeaves.length} pending approval
              {pendingLeaves.length !== 1 ? "s" : ""}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Loading requests...</p>
              </div>
            ) : pendingLeaves.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle2 className="h-12 w-12 text-success mx-auto mb-4" />
                <p className="text-foreground font-medium">All caught up!</p>
                <p className="text-muted-foreground text-sm">
                  No pending leave requests
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Applied On</TableHead>
                      <TableHead>Leave Type</TableHead>
                      <TableHead>Leave Period</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Requested Managers</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingLeaves.map((leave) => (
                      <TableRow key={leave.id}>
                        <TableCell className="font-medium">
                          {leave.employeeName ||
                            (leave.employee
                              ? `${leave.employee.firstName} ${leave.employee.lastName}`
                              : "Unknown")}
                        </TableCell>
                        <TableCell>{leave?.employee?.email}</TableCell>
                        <TableCell>
                          {new Date(
                            leave.createdAt || leave.appliedDate || "",
                          ).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{formatLeaveType(leave.leaveType)}</TableCell>
                        <TableCell>
                          {new Date(leave.startDate).toLocaleDateString()} -{" "}
                          {new Date(leave.endDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{leave.totalDays}</TableCell>
                        <TableCell className="max-w-xs truncate">
                          {(() => {
                            const managers = leave.managers;
                            if (managers && Array.isArray(managers)) {
                              const nonHrManagers = managers.filter(
                                (m) => m.manager?.role !== "HR",
                              );
                              if (nonHrManagers.length > 0) {
                                return nonHrManagers
                                  .map((m) => {
                                    const mgr = m.manager;
                                    const fullName =
                                      `${mgr.firstName || ""} ${mgr.lastName || ""}`.trim();
                                    return fullName || mgr.name || "Unknown";
                                  })
                                  .join(", ");
                              }
                            }
                            // Fallback to single manager if no non-HR managers found in list
                            return leave.manager ? leave.manager.name : "N/A";
                          })()}
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {leave.reason}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={leave.status} />
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex gap-2 justify-center">
                            <Button
                              size="sm"
                              onClick={() => handleApprove(leave)}
                            >
                              <CheckCircle2 className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => {
                                setSelectedLeave(leave);
                                setShowRejectDialog(true);
                              }}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle>Past Requests</CardTitle>
            <CardDescription>
              History of requests you have approved, rejected or cancelled
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoadingPast ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">
                  Loading past requests...
                </p>
              </div>
            ) : pastLeaves.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground text-sm">
                  No past requests found.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Leave Type</TableHead>
                      <TableHead>Leave Period</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Action Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pastLeaves.map((leave) => (
                      <TableRow key={leave.id}>
                        <TableCell className="font-medium">
                          {leave.employeeName ||
                            (leave.employee
                              ? `${leave.employee.firstName} ${leave.employee.lastName}`
                              : "Unknown")}
                        </TableCell>
                        <TableCell>{leave.employee?.email}</TableCell>
                        <TableCell>{formatLeaveType(leave.leaveType)}</TableCell>
                        <TableCell>
                          {new Date(leave.startDate).toLocaleDateString()} -{" "}
                          {new Date(leave.endDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{leave.totalDays} days</TableCell>
                        <TableCell>
                          {leave.managerRemark || leave.rejectionReason ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="cursor-help w-fit">
                                    <StatusBadge status={leave.status} />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-sm break-words">
                                    {leave.managerRemark ||
                                      leave.rejectionReason}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <StatusBadge status={leave.status} />
                          )}
                        </TableCell>
                        <TableCell>
                          {leave.updatedAt
                            ? new Date(leave.updatedAt).toLocaleDateString()
                            : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Leave Request</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this leave request
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rejectReason">Reason for Rejection</Label>
              <Textarea
                id="rejectReason"
                placeholder="Enter reason..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={4}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowRejectDialog(false)}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleReject}>
                Confirm Rejection
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
